import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import { computeGrossMargin } from './computeGrossMargin';

export interface FinancialRiskResult {
  /**
   * Σ |tvcTotal| across all inputs — always ≥ 0.
   * Represents the total committed input spend (absolute dollars).
   */
  totalSpend: number;
  /**
   * Σ gm[1][1] across all inputs — mid-yield × mid-price gross margin.
   * Positive = net profit at expected conditions; negative = net loss.
   */
  estNetGrossMargin: number;
  /**
   * Break-even yields [Low, Mid, High] at each price scenario.
   * Entry i = totalSpend / (totalRevenueArea × weightedRefPrices[i]).
   * 0 when the corresponding price is 0 or no production area exists.
   *
   * Higher price → lower break-even yield (easier to cover costs).
   */
  breakEvenYields: [number, number, number];
  /**
   * Break-even prices [Low, Mid, High] at each yield scenario.
   * Entry i = totalSpend / (totalRevenueArea × weightedRefYields[i]).
   * 0 when the corresponding yield is 0 or no production area exists.
   *
   * Higher yield → lower break-even price (easier to cover costs).
   */
  breakEvenPrices: [number, number, number];
  /** Σ revenueArea across all inputs with production data (ha). */
  totalRevenueArea: number;
  /**
   * Area-weighted reference yields [Low, Mid, High] across all inputs.
   * All zeros when no production data exists.
   */
  weightedRefYields: [number, number, number];
  /**
   * Area-weighted reference prices [Low, Mid, High] across all inputs.
   * All zeros when no production data exists.
   */
  weightedRefPrices: [number, number, number];
  /** Yield unit from the first input that has a production step (e.g. "t"). */
  yieldUnit: string;
  /** False when no inputs have any cost or production data. */
  hasData: boolean;
  /**
   * Number of inputs excluded from the yield/price/area aggregation because
   * their yield unit differed from the first unit seen.
   */
  skippedUnitCount: number;
}

/**
 * Aggregate financial risk metrics across all scoped inputs.
 *
 * ── Aggregation ───────────────────────────────────────────────────────────────
 * Calls computeGrossMargin for each input, restricted to `categories` (Plan
 * only, Actual only, or both). Results are summed directly — no averaging,
 * no de-duplication.
 *
 * ── Break-even formulas (all three scenarios) ─────────────────────────────────
 * Break-even yield at price scenario i:
 *   beYield[i] = totalSpend / (totalRevenueArea × weightedRefPrices[i])
 *
 * Break-even price at yield scenario i:
 *   bePrice[i] = totalSpend / (totalRevenueArea × weightedRefYields[i])
 *
 * estNetGrossMargin uses the mid scenario (index 1) for both yield and price.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeFinancialRisk = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): FinancialRiskResult => {
  let totalSpend = 0;
  let totalNetGM = 0;
  let totalRevenueArea = 0;
  const wYields: [number, number, number] = [0, 0, 0];
  const wPrices: [number, number, number] = [0, 0, 0];
  let yieldUnit = 't';
  let yieldUnitSet = false;
  let hasData = false;
  let skippedUnitCount = 0;

  for (const input of inputs) {
    const result = computeGrossMargin(input, categories, gUnits);
    if (!result.hasData) continue;

    hasData = true;

    // tvcTotal is negative (costs are subtracted) — negate to get positive spend
    totalSpend += -result.tvcTotal;

    // gm[1][1] = revenue[1][1] + tvcTotal = mid-yield × mid-price GM per input
    totalNetGM += result.gm[1][1];

    if (result.revenueArea > 0) {
      // Capture the first yield unit encountered.
      if (!yieldUnitSet) {
        yieldUnit = result.yieldUnit;
        yieldUnitSet = true;
      }
      // Only aggregate area/yield/price for inputs whose unit matches the
      // first one seen — mixing incommensurable units (e.g. t vs kg) would
      // make the aggregated yield axis and break-even values meaningless.
      // totalSpend and estNetGM are pure dollar values and are always included.
      if (result.yieldUnit === yieldUnit) {
        totalRevenueArea += result.revenueArea;
        // Area-weight the per-input averages back to raw sums for cross-input aggregation
        for (let i = 0; i < 3; i++) {
          wYields[i] += result.revenueArea * result.refYields[i];
          wPrices[i] += result.revenueArea * result.refPrices[i];
        }
      } else {
        skippedUnitCount += 1;
      }
    }
  }

  const weightedRefYields: [number, number, number] =
    totalRevenueArea > 0
      ? [
          wYields[0] / totalRevenueArea,
          wYields[1] / totalRevenueArea,
          wYields[2] / totalRevenueArea,
        ]
      : [0, 0, 0];

  const weightedRefPrices: [number, number, number] =
    totalRevenueArea > 0
      ? [
          wPrices[0] / totalRevenueArea,
          wPrices[1] / totalRevenueArea,
          wPrices[2] / totalRevenueArea,
        ]
      : [0, 0, 0];

  // Break-even yield per price scenario: higher price → lower break-even yield
  const breakEvenYields = weightedRefPrices.map((p) =>
    totalRevenueArea > 0 && p > 0 ? totalSpend / (totalRevenueArea * p) : 0
  ) as [number, number, number];

  // Break-even price per yield scenario: higher yield → lower break-even price
  const breakEvenPrices = weightedRefYields.map((y) =>
    totalRevenueArea > 0 && y > 0 ? totalSpend / (totalRevenueArea * y) : 0
  ) as [number, number, number];

  return {
    totalSpend,
    estNetGrossMargin: totalNetGM,
    breakEvenYields,
    breakEvenPrices,
    totalRevenueArea,
    weightedRefYields,
    weightedRefPrices,
    yieldUnit,
    hasData,
    skippedUnitCount,
  };
};
